// src/pages/trading-portal/TradeExecution/RiskControlsPage.js
// Risk Controls page with 3-tab interface: Risk Profiles, Kill Switch, Risk Check Logs

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    Switch,
    FormControlLabel,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    RiskCheckType, RiskCheckTypeLabels, RiskCheckTypeColors,
    RiskCheckResult, RiskCheckResultLabels, RiskCheckResultColors,
    KillSwitchTrigger, KillSwitchTriggerLabels, KillSwitchTriggerColors,
    KillSwitchAction, KillSwitchActionLabels, KillSwitchActionColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const EMPTY_PROFILE_FORM = {
    profile_name: '',
    max_order_size: '',
    max_order_value: '',
    max_daily_orders: '',
    max_daily_volume: '',
    max_position_size_pct: '',
    max_sector_concentration_pct: '',
    max_single_symbol_pct: '',
    fat_finger_threshold_pct: '',
    buying_power_buffer_pct: '',
    margin_utilization_limit_pct: '',
    orders_per_second: '',
    orders_per_minute: '',
    kill_switch_enabled: false,
    daily_loss_limit: '',
    max_drawdown_pct: '',
    is_default: false,
};

const RiskControlsPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: RISK PROFILES ====================
    const [profiles, setProfiles] = useState([]);
    const [profilesLoading, setProfilesLoading] = useState(true);

    // ==================== TAB 1: KILL SWITCH ====================
    const [killSwitchStatus, setKillSwitchStatus] = useState(null);
    const [killSwitchLoading, setKillSwitchLoading] = useState(false);
    const [killSwitchHistory, setKillSwitchHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // ==================== TAB 2: RISK CHECK LOGS ====================
    const [riskLogs, setRiskLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPage, setLogsPage] = useState(0);
    const [logsPerPage, setLogsPerPage] = useState(10);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsFilterType, setLogsFilterType] = useState('');
    const [logsFilterResult, setLogsFilterResult] = useState('');

    // ==================== DIALOGS ====================
    // Add/Edit Risk Profile Dialog
    const [profileDialog, setProfileDialog] = useState(false);
    const [profileEditing, setProfileEditing] = useState(null);
    const [profileForm, setProfileForm] = useState({ ...EMPTY_PROFILE_FORM });

    // Delete Confirmation Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Kill Switch Activation Confirmation Dialog
    const [killSwitchConfirmDialog, setKillSwitchConfirmDialog] = useState(false);

    // ==================== FORMAT HELPERS ====================
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
    const formatPercent = (pct) => pct != null ? `${parseFloat(pct)}%` : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-tee-risk-controls', 'view'),
                hasPermission('tr-tee-risk-controls', 'add'),
                hasPermission('tr-tee-risk-controls', 'edit'),
                hasPermission('tr-tee-risk-controls', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: RISK PROFILES ====================
    const fetchProfiles = useCallback(async () => {
        if (!canView) return;
        setProfilesLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/trade-execution/risk-profiles');
            if (res.data.success) {
                setProfiles(res.data.data || []);
            } else {
                setError(res.data.message || 'Failed to fetch risk profiles');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch risk profiles');
        } finally {
            setProfilesLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchProfiles();
    }, [fetchProfiles, canView, activeTab]);

    // ==================== TAB 1: KILL SWITCH ====================
    const fetchKillSwitchStatus = useCallback(async () => {
        if (!canView) return;
        setKillSwitchLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/trade-execution/kill-switch');
            if (res.data.success) {
                setKillSwitchStatus(res.data.data || null);
            } else {
                setError(res.data.message || 'Failed to fetch kill switch status');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch kill switch status');
        } finally {
            setKillSwitchLoading(false);
        }
    }, [canView]);

    const fetchKillSwitchHistory = useCallback(async () => {
        if (!canView) return;
        setHistoryLoading(true);
        try {
            const res = await axiosInstance.get('/api/trade-execution/kill-switch/history');
            if (res.data.success) {
                setKillSwitchHistory(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch kill switch history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView && activeTab === 1) {
            fetchKillSwitchStatus();
            fetchKillSwitchHistory();
        }
    }, [fetchKillSwitchStatus, fetchKillSwitchHistory, canView, activeTab]);

    // ==================== TAB 2: RISK CHECK LOGS ====================
    const fetchRiskLogs = useCallback(async () => {
        if (!canView) return;
        setLogsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: logsPage + 1, per_page: logsPerPage });
            if (logsFilterType) params.append('check_type', logsFilterType);
            if (logsFilterResult) params.append('check_result', logsFilterResult);
            const res = await axiosInstance.get(`/api/trade-execution/risk-check-logs?${params.toString()}`);
            if (res.data.success) {
                setRiskLogs(res.data.data || []);
                setLogsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch risk check logs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch risk check logs');
        } finally {
            setLogsLoading(false);
        }
    }, [canView, logsPage, logsPerPage, logsFilterType, logsFilterResult]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchRiskLogs();
    }, [fetchRiskLogs, canView, activeTab]);

    // ==================== PROFILE ACTIONS ====================
    const handleOpenProfileDialog = (profile = null) => {
        if (profile) {
            setProfileEditing(profile);
            setProfileForm({
                profile_name: profile.profile_name || '',
                max_order_size: profile.max_order_size ?? '',
                max_order_value: profile.max_order_value ?? '',
                max_daily_orders: profile.max_daily_orders ?? '',
                max_daily_volume: profile.max_daily_volume ?? '',
                max_position_size_pct: profile.max_position_size_pct ?? '',
                max_sector_concentration_pct: profile.max_sector_concentration_pct ?? '',
                max_single_symbol_pct: profile.max_single_symbol_pct ?? '',
                fat_finger_threshold_pct: profile.fat_finger_threshold_pct ?? '',
                buying_power_buffer_pct: profile.buying_power_buffer_pct ?? '',
                margin_utilization_limit_pct: profile.margin_utilization_limit_pct ?? '',
                orders_per_second: profile.orders_per_second ?? '',
                orders_per_minute: profile.orders_per_minute ?? '',
                kill_switch_enabled: profile.kill_switch_enabled || false,
                daily_loss_limit: profile.daily_loss_limit ?? '',
                max_drawdown_pct: profile.max_drawdown_pct ?? '',
                is_default: profile.is_default || false,
            });
        } else {
            setProfileEditing(null);
            setProfileForm({ ...EMPTY_PROFILE_FORM });
        }
        setProfileDialog(true);
    };

    const handleProfileFormChange = (field, value) => {
        setProfileForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveProfile = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                profile_name: profileForm.profile_name,
                max_order_size: profileForm.max_order_size ? Number(profileForm.max_order_size) : null,
                max_order_value: profileForm.max_order_value ? Number(profileForm.max_order_value) : null,
                max_daily_orders: profileForm.max_daily_orders ? Number(profileForm.max_daily_orders) : null,
                max_daily_volume: profileForm.max_daily_volume ? Number(profileForm.max_daily_volume) : null,
                max_position_size_pct: profileForm.max_position_size_pct ? Number(profileForm.max_position_size_pct) : null,
                max_sector_concentration_pct: profileForm.max_sector_concentration_pct ? Number(profileForm.max_sector_concentration_pct) : null,
                max_single_symbol_pct: profileForm.max_single_symbol_pct ? Number(profileForm.max_single_symbol_pct) : null,
                fat_finger_threshold_pct: profileForm.fat_finger_threshold_pct ? Number(profileForm.fat_finger_threshold_pct) : null,
                buying_power_buffer_pct: profileForm.buying_power_buffer_pct ? Number(profileForm.buying_power_buffer_pct) : null,
                margin_utilization_limit_pct: profileForm.margin_utilization_limit_pct ? Number(profileForm.margin_utilization_limit_pct) : null,
                orders_per_second: profileForm.orders_per_second ? Number(profileForm.orders_per_second) : null,
                orders_per_minute: profileForm.orders_per_minute ? Number(profileForm.orders_per_minute) : null,
                kill_switch_enabled: profileForm.kill_switch_enabled,
                daily_loss_limit: profileForm.daily_loss_limit ? Number(profileForm.daily_loss_limit) : null,
                max_drawdown_pct: profileForm.max_drawdown_pct ? Number(profileForm.max_drawdown_pct) : null,
                is_default: profileForm.is_default,
            };
            let res;
            if (profileEditing) {
                res = await axiosInstance.put(`/api/trade-execution/risk-profiles/${profileEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/trade-execution/risk-profiles', payload);
            }
            if (res.data.success) {
                setProfileDialog(false);
                setSnackbar({ open: true, message: `Risk profile ${profileEditing ? 'updated' : 'created'} successfully`, severity: 'success' });
                fetchProfiles();
            } else {
                setError(res.data.message || 'Failed to save risk profile');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save risk profile');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteProfile = async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.delete(`/api/trade-execution/risk-profiles/${deleteTarget.id}`);
            if (res.data.success) {
                setDeleteDialog(false);
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Risk profile deleted', severity: 'success' });
                fetchProfiles();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to delete profile', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete profile', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetDefault = async (profileId) => {
        try {
            const res = await axiosInstance.post(`/api/trade-execution/risk-profiles/${profileId}/set-default`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Default profile updated', severity: 'success' });
                fetchProfiles();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to set default', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to set default', severity: 'error' });
        }
    };

    // ==================== KILL SWITCH ACTIONS ====================
    const handleActivateKillSwitch = async () => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post('/api/trade-execution/kill-switch/activate');
            if (res.data.success) {
                setKillSwitchConfirmDialog(false);
                setSnackbar({ open: true, message: 'Kill switch activated - all open orders cancelled', severity: 'warning' });
                fetchKillSwitchStatus();
                fetchKillSwitchHistory();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to activate kill switch', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to activate kill switch', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeactivateKillSwitch = async () => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post('/api/trade-execution/kill-switch/deactivate');
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Kill switch deactivated', severity: 'success' });
                fetchKillSwitchStatus();
                fetchKillSwitchHistory();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to deactivate kill switch', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to deactivate kill switch', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== RENDER: LOADING SKELETON ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    // ==================== RENDER: TAB 0 - RISK PROFILES ====================
    const renderRiskProfiles = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Risk Profiles
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchProfiles}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleOpenProfileDialog()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Add Profile
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {profilesLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Profile Name</TableCell>
                                    <TableCell sx={headCellSx}>Default</TableCell>
                                    <TableCell sx={headCellSx}>Max Order Size</TableCell>
                                    <TableCell sx={headCellSx}>Max Order Value</TableCell>
                                    <TableCell sx={headCellSx}>Max Daily Orders</TableCell>
                                    <TableCell sx={headCellSx}>Fat Finger %</TableCell>
                                    <TableCell sx={headCellSx}>Kill Switch</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {profiles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.Shield sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No risk profiles configured
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Create a risk profile to enforce trading limits
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : profiles.map((profile) => (
                                    <TableRow key={profile.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {profile.profile_name || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {profile.is_default ? (
                                                <Chip
                                                    label="Default"
                                                    size="small"
                                                    color="success"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            ) : (
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.82rem' }}>-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatNumber(profile.max_order_size)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatPrice(profile.max_order_value)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatNumber(profile.max_daily_orders)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatPercent(profile.fat_finger_threshold_pct)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={profile.kill_switch_enabled ? 'Enabled' : 'Disabled'}
                                                size="small"
                                                color={profile.kill_switch_enabled ? 'error' : 'default'}
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenProfileDialog(profile)}>
                                                            <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => { setDeleteTarget(profile); setDeleteDialog(true); }}>
                                                            <MuiIcons.Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canEdit && !profile.is_default && (
                                                    <Tooltip title="Set as Default">
                                                        <IconButton size="small" onClick={() => handleSetDefault(profile.id)}>
                                                            <MuiIcons.Star sx={{ fontSize: 18, color: ACCENT }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - KILL SWITCH ====================
    const isKillSwitchActive = killSwitchStatus?.is_active || killSwitchStatus?.status === 'ACTIVE';

    const renderKillSwitch = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Kill Switch
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={() => { fetchKillSwitchStatus(); fetchKillSwitchHistory(); }}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Kill Switch Status Panel */}
            <Paper
                sx={{
                    borderRadius: '16px',
                    border: `2px solid ${isKillSwitchActive ? '#ef4444' : '#22c55e'}`,
                    boxShadow: 'none',
                    overflow: 'hidden',
                    mb: 3,
                    background: isKillSwitchActive
                        ? 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)'
                        : 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                }}
            >
                {killSwitchLoading ? renderSkeleton(3) : (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '14px',
                                        bgcolor: isKillSwitchActive ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <MuiIcons.PowerSettingsNew sx={{ fontSize: 36, color: isKillSwitchActive ? '#ef4444' : '#22c55e' }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: isKillSwitchActive ? '#dc2626' : '#16a34a', lineHeight: 1.2 }}>
                                        {isKillSwitchActive ? 'ACTIVE' : 'INACTIVE'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem', mt: 0.25 }}>
                                        Kill Switch Status
                                    </Typography>
                                </Box>
                            </Box>

                            <Box>
                                {isKillSwitchActive ? (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleDeactivateKillSwitch}
                                        disabled={submitting || !canEdit}
                                        startIcon={<MuiIcons.PowerSettingsNew />}
                                        sx={{
                                            fontFamily,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            bgcolor: '#22c55e',
                                            '&:hover': { bgcolor: '#16a34a' },
                                            px: 3,
                                            py: 1.2,
                                            fontSize: '0.95rem',
                                            borderRadius: '12px',
                                        }}
                                    >
                                        {submitting ? 'Deactivating...' : 'Deactivate Kill Switch'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => setKillSwitchConfirmDialog(true)}
                                        disabled={submitting || !canEdit}
                                        startIcon={<MuiIcons.PowerSettingsNew />}
                                        sx={{
                                            fontFamily,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            bgcolor: '#ef4444',
                                            '&:hover': { bgcolor: '#dc2626' },
                                            px: 3,
                                            py: 1.2,
                                            fontSize: '0.95rem',
                                            borderRadius: '12px',
                                        }}
                                    >
                                        Activate Kill Switch
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {/* Show active event details */}
                        {isKillSwitchActive && killSwitchStatus && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isKillSwitchActive ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}` }}>
                                <Grid container spacing={2}>
                                    {killSwitchStatus.trigger_type && (
                                        <Grid item xs={12} sm={4}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trigger Type</Typography>
                                            <Chip
                                                label={KillSwitchTriggerLabels[killSwitchStatus.trigger_type] || killSwitchStatus.trigger_type}
                                                size="small"
                                                color={KillSwitchTriggerColors[killSwitchStatus.trigger_type] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }}
                                            />
                                        </Grid>
                                    )}
                                    {killSwitchStatus.activated_at && (
                                        <Grid item xs={12} sm={4}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activated At</Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem', mt: 0.5 }}>
                                                {formatDateTime(killSwitchStatus.activated_at)}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {killSwitchStatus.orders_cancelled != null && (
                                        <Grid item xs={12} sm={4}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Orders Cancelled</Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#ef4444', fontSize: '1.1rem', mt: 0.5 }}>
                                                {killSwitchStatus.orders_cancelled}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>

            {/* Kill Switch History Table */}
            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem', mb: 2 }}>
                Kill Switch History
            </Typography>
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {historyLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Trigger Type</TableCell>
                                    <TableCell sx={headCellSx}>Action Taken</TableCell>
                                    <TableCell sx={headCellSx}>Orders Cancelled</TableCell>
                                    <TableCell sx={headCellSx}>Trigger Value</TableCell>
                                    <TableCell sx={headCellSx}>Activated At</TableCell>
                                    <TableCell sx={headCellSx}>Deactivated At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {killSwitchHistory.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.History sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No kill switch events recorded
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : killSwitchHistory.map((event, idx) => (
                                    <TableRow key={event.id || idx} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell>
                                            <Chip
                                                label={KillSwitchTriggerLabels[event.trigger_type] || event.trigger_type}
                                                size="small"
                                                color={KillSwitchTriggerColors[event.trigger_type] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={KillSwitchActionLabels[event.action_taken] || event.action_taken}
                                                size="small"
                                                color={KillSwitchActionColors[event.action_taken] || 'default'}
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {event.orders_cancelled != null ? event.orders_cancelled : '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                            {event.trigger_value != null ? event.trigger_value : '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            {formatDateTime(event.activated_at)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            {formatDateTime(event.deactivated_at)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 2 - RISK CHECK LOGS ====================
    const renderRiskCheckLogs = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Risk Check Logs
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchRiskLogs}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontFamily }}>Check Type</InputLabel>
                    <Select
                        value={logsFilterType}
                        onChange={(e) => { setLogsFilterType(e.target.value); setLogsPage(0); }}
                        label="Check Type"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All Types</MenuItem>
                        {Object.entries(RiskCheckType).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{RiskCheckTypeLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Result</InputLabel>
                    <Select
                        value={logsFilterResult}
                        onChange={(e) => { setLogsFilterResult(e.target.value); setLogsPage(0); }}
                        label="Result"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All Results</MenuItem>
                        {Object.entries(RiskCheckResult).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{RiskCheckResultLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {logsLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Order ID</TableCell>
                                        <TableCell sx={headCellSx}>Check Type</TableCell>
                                        <TableCell sx={headCellSx}>Result</TableCell>
                                        <TableCell sx={headCellSx}>Threshold</TableCell>
                                        <TableCell sx={headCellSx}>Actual</TableCell>
                                        <TableCell sx={headCellSx}>Override By</TableCell>
                                        <TableCell sx={headCellSx}>Timestamp</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {riskLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.FactCheck sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No risk check logs found
                                                </Typography>
                                                <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                    Logs will appear here as orders are validated
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : riskLogs.map((log) => (
                                        <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {log.order_id || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={RiskCheckTypeLabels[log.check_type] || log.check_type}
                                                    size="small"
                                                    color={RiskCheckTypeColors[log.check_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={RiskCheckResultLabels[log.check_result] || log.check_result}
                                                    size="small"
                                                    color={RiskCheckResultColors[log.check_result] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {log.threshold != null ? log.threshold : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {log.actual != null ? log.actual : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {log.override_by || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                {formatDateTime(log.timestamp || log.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={logsTotal}
                            page={logsPage}
                            onPageChange={(_, newPage) => setLogsPage(newPage)}
                            rowsPerPage={logsPerPage}
                            onRowsPerPageChange={(e) => { setLogsPerPage(parseInt(e.target.value, 10)); setLogsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: ADD/EDIT RISK PROFILE DIALOG ====================
    const renderProfileDialog = () => (
        <Dialog open={profileDialog} onClose={() => setProfileDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                {profileEditing ? 'Edit Risk Profile' : 'Add Risk Profile'}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                <Grid container spacing={2}>
                    {/* Profile Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Profile Name"
                            value={profileForm.profile_name}
                            onChange={(e) => handleProfileFormChange('profile_name', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Order Limits */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Order Limits
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Order Size"
                            type="number"
                            value={profileForm.max_order_size}
                            onChange={(e) => handleProfileFormChange('max_order_size', e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Order Value"
                            type="number"
                            value={profileForm.max_order_value}
                            onChange={(e) => handleProfileFormChange('max_order_value', e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Daily Orders"
                            type="number"
                            value={profileForm.max_daily_orders}
                            onChange={(e) => handleProfileFormChange('max_daily_orders', e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Daily Volume"
                            type="number"
                            value={profileForm.max_daily_volume}
                            onChange={(e) => handleProfileFormChange('max_daily_volume', e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Position Limits */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Position Limits
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Position Size %"
                            type="number"
                            value={profileForm.max_position_size_pct}
                            onChange={(e) => handleProfileFormChange('max_position_size_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Sector Concentration %"
                            type="number"
                            value={profileForm.max_sector_concentration_pct}
                            onChange={(e) => handleProfileFormChange('max_sector_concentration_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Single Symbol %"
                            type="number"
                            value={profileForm.max_single_symbol_pct}
                            onChange={(e) => handleProfileFormChange('max_single_symbol_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Validation */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Validation
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Fat Finger Threshold %"
                            type="number"
                            value={profileForm.fat_finger_threshold_pct}
                            onChange={(e) => handleProfileFormChange('fat_finger_threshold_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Buying Power Buffer %"
                            type="number"
                            value={profileForm.buying_power_buffer_pct}
                            onChange={(e) => handleProfileFormChange('buying_power_buffer_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Margin Utilization Limit %"
                            type="number"
                            value={profileForm.margin_utilization_limit_pct}
                            onChange={(e) => handleProfileFormChange('margin_utilization_limit_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Speed Limits */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Speed Limits
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Orders Per Second"
                            type="number"
                            value={profileForm.orders_per_second}
                            onChange={(e) => handleProfileFormChange('orders_per_second', e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Orders Per Minute"
                            type="number"
                            value={profileForm.orders_per_minute}
                            onChange={(e) => handleProfileFormChange('orders_per_minute', e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Kill Switch */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Kill Switch
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={profileForm.kill_switch_enabled}
                                    onChange={(e) => handleProfileFormChange('kill_switch_enabled', e.target.checked)}
                                    color="error"
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Enabled</Typography>}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Daily Loss Limit"
                            type="number"
                            value={profileForm.daily_loss_limit}
                            onChange={(e) => handleProfileFormChange('daily_loss_limit', e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            disabled={!profileForm.kill_switch_enabled}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Drawdown %"
                            type="number"
                            value={profileForm.max_drawdown_pct}
                            onChange={(e) => handleProfileFormChange('max_drawdown_pct', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            disabled={!profileForm.kill_switch_enabled}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>

                    {/* Section: Default Profile */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem', mt: 1, mb: 0.5 }}>
                            Default Profile
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={profileForm.is_default}
                                    onChange={(e) => handleProfileFormChange('is_default', e.target.checked)}
                                    color="warning"
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Is Default</Typography>}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setProfileDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveProfile}
                    variant="contained"
                    disabled={submitting || !profileForm.profile_name}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Saving...' : (profileEditing ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: DELETE CONFIRMATION DIALOG ====================
    const renderDeleteDialog = () => (
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                Confirm Delete
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                    Are you sure you want to delete the risk profile <strong>{deleteTarget?.profile_name}</strong>? This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleDeleteProfile}
                    variant="contained"
                    color="error"
                    disabled={submitting}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    {submitting ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: KILL SWITCH ACTIVATION CONFIRMATION DIALOG ====================
    const renderKillSwitchConfirmDialog = () => (
        <Dialog open={killSwitchConfirmDialog} onClose={() => setKillSwitchConfirmDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#ef4444', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                <MuiIcons.Warning sx={{ color: '#ef4444' }} />
                Activate Kill Switch
            </DialogTitle>
            <DialogContent>
                <Alert severity="error" sx={{ fontFamily, mb: 2 }}>
                    This will immediately cancel ALL open orders.
                </Alert>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                    Are you sure you want to activate the kill switch? This action will cancel all open orders across all broker connections and halt all trading activity until the kill switch is deactivated.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setKillSwitchConfirmDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleActivateKillSwitch}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#ef4444',
                        '&:hover': { bgcolor: '#dc2626' },
                    }}
                >
                    {submitting ? 'Activating...' : 'Confirm Activate'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view risk controls. Required permission: <strong>tr-tee-risk-controls</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <MuiIcons.Shield sx={{ fontSize: 28, color: ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.4rem', lineHeight: 1.2 }}>
                        Risk Controls
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                        Manage risk profiles, kill switch, and monitor risk check activity
                    </Typography>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.Security sx={{ fontSize: 18 }} />} iconPosition="start" label="Risk Profiles" />
                    <Tab icon={<MuiIcons.PowerSettingsNew sx={{ fontSize: 18 }} />} iconPosition="start" label="Kill Switch" />
                    <Tab icon={<MuiIcons.FactCheck sx={{ fontSize: 18 }} />} iconPosition="start" label="Risk Check Logs" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderRiskProfiles()}
            {activeTab === 1 && renderKillSwitch()}
            {activeTab === 2 && renderRiskCheckLogs()}

            {/* Dialogs */}
            {renderProfileDialog()}
            {renderDeleteDialog()}
            {renderKillSwitchConfirmDialog()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RiskControlsPage;
